import { 
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  Link2, 
  FileText, 
  Search, 
  Globe,
  Users,
  Settings,
  Code,
  Shield,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { UserProfileCard } from "./UserProfileCard";
import { UsageCard } from "./UsageCard";
import { useAdmin } from "@/hooks/useAdmin";
import { useNavigate } from "react-router-dom";

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface AppSidebarProps {
  user: User;
  website: WebsiteData;
  currentTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
  hasActiveSubscription?: boolean;
}

const menuItems = [
  {
    title: "Dashboard",
    icon: BarChart3,
    value: "overview",
  },
  {
    title: "My Content",
    icon: FileText,
    value: "content",
  },
  {
    title: "LLMs.txt",
    icon: Code,
    value: "llms-txt",
  },
  {
    title: "Backlinks", 
    icon: Link2,
    value: "backlinks",
  },
  {
    title: "Google Index",
    icon: Globe,
    value: "google-index",
  },
  {
    title: "Visitors",
    icon: Users,
    value: "visitors",
    disabled: true,
    badge: "Soon"
  },
  {
    title: "Settings",
    icon: Settings,
    value: "settings",
  },
];

export function AppSidebar({ user, website, currentTab, onTabChange, className, hasActiveSubscription = true }: AppSidebarProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  
  // Settings is always accessible, other tabs require subscription
  const isItemLocked = (itemValue: string) => {
    if (itemValue === "settings") return false;
    return !hasActiveSubscription;
  };



  return (
    <Sidebar className={`border-r border-sidebar-border ${className || ''}`}>
      <SidebarHeader className="p-6 pb-2">
        {/* Brand */}
        <div className="mb-6">
          <div className="h-12 w-12 rounded-lg overflow-hidden bg-background/95">
            <img
              src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png"
              alt="Seoro Logo"
              className="h-full w-full object-contain"
            />
          </div>
        </div>

        {/* Website info */}
        {website && !isCollapsed && (
          <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-sidebar-accent/70 border border-sidebar-border/30 mb-4">
            <img
              src={website.favicon_url || '/placeholder.svg'}
              alt="Site favicon"
              className="w-7 h-7 rounded"
              onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
            />
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium text-sidebar-foreground text-sm">{website.domain}</div>
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="relative">
        <SidebarGroup className="relative">
          <SidebarGroupContent className="relative">
            <SidebarMenu className="space-y-1 relative">
              {menuItems.map((item) => {
                const isLocked = isItemLocked(item.value);
                const isDisabled = item.disabled || isLocked;
                
                return (
                  <SidebarMenuItem key={item.value}>
                    <SidebarMenuButton
                      onClick={() => !isDisabled && onTabChange(item.value)}
                      isActive={currentTab === item.value}
                      disabled={isDisabled}
                      className={`w-full justify-start gap-3 rounded-xl font-medium text-sm transition-all duration-200 px-3 py-2.5 ${
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                          : currentTab === item.value
                          ? 'bg-primary/10 text-primary border border-primary/20 shadow-sm'
                          : 'hover:bg-sidebar-accent/50 text-sidebar-foreground hover:text-foreground'
                      }`}
                    >
                      <item.icon className={`h-4 w-4 ${
                        currentTab === item.value ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      {!isCollapsed && (
                        <>
                          <span className="font-medium">{item.title}</span>
                          {item.badge && (
                            <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium border border-primary/20">{item.badge}</span>
                          )}
                          {isLocked && !item.badge && (
                            <span className="ml-auto text-xs bg-muted/80 text-muted-foreground px-2 py-0.5 rounded-full font-medium border border-border/30">Locked</span>
                          )}
                        </>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Dashboard Button - Only visible to admins */}
        {isAdmin && (
          <SidebarGroup className="mt-4">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => navigate('/admin')}
                    className="w-full justify-start gap-3 rounded-xl font-medium text-sm transition-all duration-200 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                  >
                    <Shield className="h-4 w-4 text-blue-600" />
                    {!isCollapsed && (
                      <span className="font-medium">Admin Dashboard</span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-6 pt-0">
        {/* Usage card */}
        <UsageCard isCollapsed={isCollapsed} hasActiveSubscription={hasActiveSubscription} user={user} />

        {/* Divider above user profile */}
        <div className="w-full h-[1px] bg-border my-2" />

        {/* User account */}
        {user && (
          <UserProfileCard user={user} isCollapsed={isCollapsed} />
        )}
      </SidebarFooter>
    </Sidebar>
  );
}